import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const isPublicPage = (): boolean => {
    try {
      const url = (router.url || window.location?.pathname || '').toLowerCase();
      return (
        url === '/' ||
        url === '/home' ||
        url.startsWith('/website') ||
        url.startsWith('/auth') ||
        url.includes('/landing') ||
        url.includes('/packages') ||
        url.includes('/register') ||
        url.includes('/teachers') ||
        url.includes('/teacher-detail') ||
        url.includes('/stages') ||
        url.startsWith('/payment-callback') ||
        url.startsWith('/payment-success')
      );
    } catch {
      return false;
    }
  };

  const isAuthEndpoint =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/Accounts/login');

  const token = authService.getToken();
  let authReq = req;

  // Attach token if request doesn't already have Authorization header and not an auth endpoint
  if (!req.headers.has('Authorization') && !req.headers.has('authorization')) {
    if (token && token.trim() && !isAuthEndpoint) {
      const cleanToken = token.startsWith('Bearer ') ? token.slice(7).trim() : token.trim();
      const formattedToken = `Bearer ${cleanToken}`;

      authReq = req.clone({
        setHeaders: {
          Authorization: formattedToken,
        },
      });
    }
  }

  return next(authReq).pipe(
    catchError((error: any) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Bypass refresh logic for auth endpoints to prevent loops
        if (isAuthEndpoint) {
          return throwError(() => error);
        }

        const refreshToken = authService.getRefreshToken();
        if (!refreshToken) {
          if (isPublicPage()) {
            authService.clearAuthStorage();
          } else {
            authService.logout('/auth/login');
          }
          return throwError(() => error);
        }

        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken().pipe(
            switchMap((res: any) => {
              isRefreshing = false;
              const authData = res?.data || res;
              const newAccessToken = authData?.accessToken || authData?.token || authService.getToken();
              const newRefreshToken = authData?.refreshToken || refreshToken;
              const expiresAt = authData?.accessTokenExpiresAt || authData?.expiresAt || '';
              const user = authData?.user;

              if (newAccessToken) {
                authService.setAuthData(newAccessToken, expiresAt, user, newRefreshToken);
              }

              refreshTokenSubject.next(newAccessToken);

              // Retry the original request with the new access token
              const formattedNewToken = (newAccessToken || '').startsWith('Bearer ')
                ? (newAccessToken || '').trim()
                : `Bearer ${(newAccessToken || '').trim()}`;

              return next(
                req.clone({
                  setHeaders: {
                    Authorization: formattedNewToken,
                  },
                })
              );
            }),
            catchError((refreshErr) => {
              isRefreshing = false;
              refreshTokenSubject.next(null);
              if (isPublicPage()) {
                authService.clearAuthStorage();
              } else {
                authService.logout('/auth/login');
              }
              return throwError(() => refreshErr);
            })
          );
        } else {
          // If a refresh is already in progress, queue and wait for the new token
          return refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((newToken) => {
              const formattedNewToken = (newToken || '').startsWith('Bearer ')
                ? (newToken || '').trim()
                : `Bearer ${(newToken || '').trim()}`;

              return next(
                req.clone({
                  setHeaders: {
                    Authorization: formattedNewToken,
                  },
                })
              );
            })
          );
        }
      }

      return throwError(() => error);
    })
  );
};

